import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import '../Style/App.css';
import { useAlert } from '../context/AlertContext';
import { useRole } from '../context/RoleContext';
import { jwtDecode } from 'jwt-decode';

// Agency domain mapping - maps email domains to agency names
const AGENCY_DOMAIN_MAPPING = {
  'hainescity.com': 'Haines City Police Department',
  'cityofbartow.net': 'Bartow Police Department',
  'mydavenport.org': 'Davenport Police Department',
  'mywinterhaven.com': 'Winter Haven Police Department',
  'polksheriff.org': 'Polk County Sheriff\'s Office',
  'auburndalefl.com': 'Auburndale Police Department',
  'lakewalesfl.gov': 'Lake Wales Police Department',
  'lakelandgov.net': 'Lakeland Police Department',
  'mylakealfred.com': 'Lake Alfred Police Department',
  'sheriffdesotofl.gov': 'Desoto County Sheriff\'s Office',
  // Add more domains as needed
};

// Helper function to get agency name from user email
const getAgencyFromEmail = (email) => {
  if (!email) {
    return 'Unknown Agency';
  }

  const domain = email.split('@')[1]?.toLowerCase();
  const agency = AGENCY_DOMAIN_MAPPING[domain] || 'Other Agency';
  return agency;
};

const UserManagement = ({ onNavigateToAuditDashboard }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const { showSuccess, showError, showConfirm } = useAlert();
  const { forceLogout } = useRole();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, approved, rejected
  const [filterAgency, setFilterAgency] = useState('all'); // all, or specific agency
  // const [auditLoading, setAuditLoading] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const token = localStorage.getItem('idToken');

  // Configure axios headers - memoized to prevent unnecessary re-renders
  const authHeader = useMemo(() => ({
    headers: {
      Authorization: token ? `Bearer ${token}` : ''
    }
  }), [token]);

  // Fetch all users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {


      const apiUrl = process.env.NODE_ENV === 'production'
        ? '/api/admin/users'
        : 'http://localhost:8080/api/admin/users';

      const response = await axios.get(apiUrl, authHeader);
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);

      // Enhanced error handling for 403
      if (err.response && err.response.status === 403) {
        const errorData = err.response.data;
        console.error('403 Forbidden Details:', errorData);
        setError(`Access denied. Required role: ${errorData.required?.join(', ') || 'Admin'}. Your current role: ${errorData.current || 'Unknown'}`);
      } else {
        setError('Failed to fetch users. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  // Update user role
  const updateUserRole = async (username, role) => {
    try {
      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/admin/users/${username}/role`
        : `http://localhost:8080/api/admin/users/${username}/role`;

      await axios.put(apiUrl, { role }, authHeader);

      // Check if the admin is updating their own role
      const token = localStorage.getItem('idToken');
      let isUpdatingOwnRole = false;

      if (token) {
        try {
          const decoded = jwtDecode(token);
          const currentUserEmail = decoded.email;
          const updatedUser = users.find(user => user.username === username);

          // Check if user is updating their own role
          if (updatedUser && updatedUser.email === currentUserEmail) {
            isUpdatingOwnRole = true;
          }
        } catch (decodeError) {
          console.error('Error checking current user:', decodeError);
        }
      }

      // Update local state
      setUsers(users.map(user =>
        user.username === username
          ? { ...user, role }
          : user
      ));

      setEditingUser(null);
      setNewRole('');

      if (isUpdatingOwnRole) {
        // Show message and force logout
        showSuccess(`Your role has been updated to ${role}. You will be logged out to apply the changes.`);

        // Wait a moment for the user to see the message, then logout
        setTimeout(() => {
          // Use the forceLogout function which handles everything
          forceLogout();
        }, 2000);
      } else {
        showSuccess(`User role updated to ${role} successfully!`);
      }
    } catch (err) {
      console.error('Error updating user role:', err);

      // Check if this is a Security Admin protection error
      if (err.response?.status === 403 && err.response?.data?.protected) {
        showError('Security Admin users are protected and cannot have their roles changed.');
      } else {
        showError('Failed to update user role. Please try again.');
      }

      // Reset editing state
      setEditingUser(null);
      setNewRole('');
    }
  };

  // Approve user
  const approveUser = async (username) => {
    try {
      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/admin/users/${username}/approve`
        : `http://localhost:8080/api/admin/users/${username}/approve`;

      await axios.post(apiUrl, {}, authHeader);

      // Update local state
      setUsers(users.map(user =>
        user.username === username
          ? { ...user, approvalStatus: 'approved', enabled: true }
          : user
      ));

      showSuccess('User approved successfully!');
    } catch (err) {
      console.error('Error approving user:', err);
      showError('Failed to approve user. Please try again.');
    }
  };

  // Reject user
  const rejectUser = async (username) => {
    const confirmed = await showConfirm(
      'Are you sure you want to reject this user? This action cannot be undone.',
      'Confirm User Rejection'
    );
    if (!confirmed) {
      return;
    }

    try {
      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/admin/users/${username}/reject`
        : `http://localhost:8080/api/admin/users/${username}/reject`;

      await axios.post(apiUrl, {}, authHeader);

      // Update local state
      setUsers(users.map(user =>
        user.username === username
          ? { ...user, approvalStatus: 'rejected', enabled: false }
          : user
      ));

      showSuccess('User rejected successfully.');
    } catch (err) {
      console.error('Error rejecting user:', err);
      showError('Failed to reject user. Please try again.');
    }
  };

  // Delete user
  const deleteUser = async (userEmail, userRole) => {
    // Check if user is Security Admin
    if (userRole === 'Security Admin') {
      showError('Security Admin users cannot be deleted for security reasons. Please contact a system administrator if this user needs to be removed.');
      return;
    }

    const confirmed = await showConfirm(
      `Are you sure you want to permanently delete the user "${userEmail}"? This action cannot be undone and will completely remove the user from the system.`,
      'Confirm User Deletion',
      'Delete',
      'Cancel'
    );
    if (!confirmed) {
      return;
    }

    try {
      // Use email as the username parameter since Cognito uses email as username
      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/admin/users/${encodeURIComponent(userEmail)}`
        : `http://localhost:8080/api/admin/users/${encodeURIComponent(userEmail)}`;

      await axios.delete(apiUrl, authHeader);

      // Remove user from local state using email to find the user
      setUsers(users.filter(user => user.email !== userEmail));

      showSuccess('User deleted successfully.');
    } catch (err) {
      console.error('Error deleting user:', err);
      console.error('Error response:', err.response?.data);

      if (err.response?.status === 404) {
        showError('User not found. They may have already been deleted.');
        // Remove from local state anyway
        setUsers(users.filter(user => user.email !== userEmail));
      } else if (err.response?.status === 403) {
        const errorMessage = err.response?.data?.error || 'Not authorized to delete this user.';
        showError(errorMessage);
      } else if (err.response?.status === 400) {
        showError('Invalid user parameter. Please try again.');
      } else {
        const errorDetails = err.response?.data?.details || err.response?.data?.error || err.message;
        showError(`Failed to delete user: ${errorDetails}`);
      }
    }
  };

  // Handle edit button click
  const handleEditRole = (user) => {
    setEditingUser(user.username);
    setNewRole(user.role);
  };

  // Generate audit report
  /*
  const generateAuditReport = async () => {
    setAuditLoading(true);
    try {
      const apiUrl = process.env.NODE_ENV === 'production'
        ? '/api/audit/reports/generate'
        : 'http://localhost:8080/api/audit/reports/generate';

      const reportData = {
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
        endDate: new Date().toISOString(),
        reportType: 'summary'
      };

      const response = await axios.post(apiUrl, reportData, authHeader);

      if (response.data.success) {
        // Create and download the report
        const reportContent = JSON.stringify(response.data.data, null, 2);
        const blob = new Blob([reportContent], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `audit-report-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showSuccess('Audit report generated and downloaded successfully!');
      } else {
        showError('Failed to generate audit report.');
      }
    } catch (err) {
      console.error('Error generating audit report:', err);
      if (err.response?.status === 403) {
        showError('You do not have permission to generate audit reports.');
      } else {
        showError('Failed to generate audit report. Please try again.');
      }
    } finally {
      setAuditLoading(false);
    }
  };
  */

  // Handle save role
  const handleSaveRole = async (username) => {
    if (newRole && newRole !== users.find(u => u.username === username)?.role) {
      // Check if user is updating their own role
      const token = localStorage.getItem('idToken');
      let isUpdatingOwnRole = false;

      if (token) {
        try {
          const decoded = jwtDecode(token);
          const currentUserEmail = decoded.email;
          const updatedUser = users.find(user => user.username === username);

          if (updatedUser && updatedUser.email === currentUserEmail) {
            isUpdatingOwnRole = true;
          }
        } catch (decodeError) {
          console.error('Error checking current user:', decodeError);
        }
      }

      // Show confirmation if user is updating their own role
      if (isUpdatingOwnRole) {
        const confirmed = await showConfirm(
          `You are about to change your own role to ${newRole}. This will log you out immediately and you'll need to sign back in with your new permissions. Are you sure you want to continue?`,
          'Confirm Role Change'
        );
        if (!confirmed) {
          return;
        }
      }

      updateUserRole(username, newRole);
    } else {
      setEditingUser(null);
      setNewRole('');
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingUser(null);
    setNewRole('');
  };

  // Handle CSV file selection
  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      setImportResults(null);
    } else {
      showError('Please select a valid CSV file.');
      setCsvFile(null);
    }
  };

  // Handle CSV import
  const handleCsvImport = async () => {
    if (!csvFile) {
      showError('Please select a CSV file first.');
      return;
    }

    const confirmed = await showConfirm(
      `Are you sure you want to import records from "${csvFile.name}"? This will add new records to the database.`,
      'Confirm CSV Import'
    );
    if (!confirmed) {
      return;
    }

    setCsvImporting(true);
    setImportResults(null);

    try {
      const formData = new FormData();
      formData.append('csvFile', csvFile);

      const apiUrl = process.env.NODE_ENV === 'production'
        ? '/api/immigrants/bulk-import'
        : 'http://localhost:8080/api/immigrants/bulk-import';

      const response = await axios.post(apiUrl, formData, {
        ...authHeader,
        headers: {
          ...authHeader.headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResults(response.data.results);
      showSuccess(`Import completed! ${response.data.results.successCount} records imported successfully.`);

      // Clear the file input
      setCsvFile(null);
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';

    } catch (err) {
      console.error('Error importing CSV:', err);
      showError('Failed to import CSV file. Please try again.');
    } finally {
      setCsvImporting(false);
    }
  };

  // Download CSV template
  const downloadCsvTemplate = () => {
    const csvHeaders = [
      'contactDate', 'ori', 'agencyName', 'officerFirstName', 'officerLastName', 'badgeOrId',
      'contactCounty', 'contactStreetAddress', 'contactApartmentUnit', 'contactCity', 'contactState', 'contactZipCode',
      'firstName', 'middleName', 'lastName', 'countryOfCitizenship', 'hasDualCitizenship', 'dualCitizenshipCountry',
      'alienRegistrationNumber', 'dateOfBirth', 'sex', 'currentStreetAddress', 'currentCity', 'currentState',
      'currentZipCode', 'phoneNumber', 'employerName', 'employerStreetAddress',
      'employerCity', 'employerState', 'employerZipCode', 'employerPhoneNumber', 'hasGangAffiliations',
      'gangName', 'vehicleYear', 'vehicleMake', 'vehicleModel',
      'vehicleTag', 'vehicleTagState', 'hasCriminalHistory',
      'citizenshipVerifiedWithIce', 'iceCallCenterContactName', 'iceCallCenterContactId',
      'wasArrested', 'arrestCharges', 'iceContacted', 'iceResponded', 'detainedByIce',
      'notDetainedReason', 'iceNoResponseReason', 'photoUrl'
    ];

    const csvContent = csvHeaders.join(',') + '\n';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'immigrant_records_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };



  // Format date - fixed to handle timezone issues
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    // If the date string is in YYYY-MM-DD format, treat it as a local date
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    // For other date formats, use the original logic
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get unique agencies from users for filter dropdown
  const uniqueAgencies = useMemo(() => {
    const agencies = [...new Set(users.map(user => getAgencyFromEmail(user.email)))];
    return agencies.sort();
  }, [users]);

  // Filter users based on search term, approval status, and agency
  const filteredUsers = users.filter(user => {
    const userAgency = getAgencyFromEmail(user.email);

    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userAgency.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.approvalStatus && user.approvalStatus.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter = filterStatus === 'all' ||
      (filterStatus === 'pending' && user.approvalStatus === 'pending') ||
      (filterStatus === 'approved' && user.approvalStatus === 'approved') ||
      (filterStatus === 'rejected' && user.approvalStatus === 'rejected');

    const matchesAgency = filterAgency === 'all' || userAgency === filterAgency;

    return matchesSearch && matchesFilter && matchesAgency;
  });

  // Get counts for different approval statuses
  const pendingUsers = users.filter(user => user.approvalStatus === 'pending');
  const pendingCount = pendingUsers.length;
  const approvedCount = users.filter(user => user.approvalStatus === 'approved').length;
  const rejectedCount = users.filter(user => user.approvalStatus === 'rejected').length;

  // Load users on component mount
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <div className="userManagement">
      <div className="userManagementHeader">
        <h2>User Management</h2>
        <div className="headerButtons">
          <button
            onClick={fetchUsers}
            className="modern-btn refresh-btn"
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh Users'}
          </button>
        </div>
      </div>

      {error && (
        <div className="errorMessage">
          {error}
        </div>
      )}

      {/* Pending Requests Section */}
      {pendingCount > 0 && (
        <div className="pendingRequestsSection">
          <div className="pendingHeader">
            <h3>Pending Approval Requests ({pendingCount})</h3>
            <p>New users waiting for admin approval</p>
          </div>

          <div className="pendingRequestsTable">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Agency</th>
                  <th>Role</th>
                  <th>Requested</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingUsers.map((user) => (
                  <tr key={user.username} className="pendingRow">
                    <td>
                      <strong>{user.email}</strong>
                    </td>
                    <td>
                      <span className="agencyTag" title={getAgencyFromEmail(user.email)}>
                        {getAgencyFromEmail(user.email)}
                      </span>
                    </td>
                    <td>
                      <span className={`roleTag role${user.role}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>{formatDate(user.created)}</td>
                    <td>
                      <div className="pendingActions">
                        <button
                          onClick={() => approveUser(user.username)}
                          className="actionBtn approveBtn"
                          title="Approve User"
                        >
                          ✓ Approve
                        </button>
                        <button
                          onClick={() => rejectUser(user.username)}
                          className="actionBtn rejectBtn"
                          title="Reject User"
                        >
                          ✗ Reject
                        </button>
                        <button
                          onClick={() => deleteUser(user.email)}
                          className="actionBtn deleteBtn"
                          title="Delete User"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="searchAndFilter">
        <div className="searchBar">
          <input
            type="text"
            placeholder="Search by email, agency, role, or status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="modern-input"
          />
        </div>

        <div className="filterBar">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="modern-input"
          >
            <option value="all">All Users ({users.length})</option>
            <option value="pending">Pending Approval ({pendingCount})</option>
            <option value="approved">Approved ({approvedCount})</option>
            <option value="rejected">Rejected ({rejectedCount})</option>
          </select>

          <select
            value={filterAgency}
            onChange={(e) => setFilterAgency(e.target.value)}
            className="modern-input"
            style={{ marginLeft: '10px' }}
          >
            <option value="all">All Agencies ({users.length})</option>
            {uniqueAgencies.map(agency => {
              const agencyCount = users.filter(user => getAgencyFromEmail(user.email) === agency).length;
              return (
                <option key={agency} value={agency}>
                  {agency} ({agencyCount})
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* All Users Section */}
      <div className="allUsersSection">
        <div className="sectionHeader">
          <h3>All Users</h3>
          <p>Complete user directory with management options</p>
        </div>

        <div className="usersTable">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Agency</th>
              <th>Role</th>
              <th>Status</th>
              <th>Approval</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" style={{textAlign: 'center'}}>
                  Loading users...
                </td>
              </tr>
            ) : filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <tr key={user.username}>
                  <td>{user.email}</td>
                  <td>
                    <span className="agencyTag" title={getAgencyFromEmail(user.email)}>
                      {getAgencyFromEmail(user.email)}
                    </span>
                  </td>
                  <td>
                    {editingUser === user.username ? (
                      <select
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value)}
                        className="modern-input"
                        style={{width: '100%', padding: '4px'}}
                      >
                        <option value="Officer">Officer</option>
                        <option value="Supervisor">Supervisor</option>
                        <option value="Admin">Admin</option>
                        <option value="Detention">Detention</option>
                      </select>
                    ) : (
                      <span className={`roleTag role${user.role}`}>
                        {user.role}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`statusTag status${user.status}`}>
                      {user.status}
                    </span>
                  </td>
                  <td>
                    <span className={`approvalTag approval${user.approvalStatus || 'approved'}`}>
                      {user.approvalStatus || 'approved'}
                    </span>
                  </td>
                  <td>{formatDate(user.created)}</td>
                  <td>
                    <div className="actionButtons">
                      {user.approvalStatus === 'pending' && (
                        <>
                          <button
                            onClick={() => approveUser(user.username)}
                            className="actionBtn approveBtn"
                            title="Approve User"
                          >
                            ✓ Approve
                          </button>
                          <button
                            onClick={() => rejectUser(user.username)}
                            className="actionBtn rejectBtn"
                            title="Reject User"
                          >
                            ✗ Reject
                          </button>
                          {user.role !== 'Security Admin' ? (
                            <button
                              onClick={() => deleteUser(user.email, user.role)}
                              className="actionBtn deleteBtn"
                              title="Delete User"
                            >
                              Delete
                            </button>
                          ) : (
                            <span
                              className="actionBtn deleteBtn disabled"
                              title="Security Admin users cannot be deleted"
                              style={{ opacity: 0.5, cursor: 'not-allowed' }}
                            >
                              🔒 Protected
                            </span>
                          )}
                        </>
                      )}

                      {user.approvalStatus === 'approved' && editingUser === user.username ? (
                        <div className="editActions">
                          <button
                            onClick={() => handleSaveRole(user.username)}
                            className="actionBtn saveBtn"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="actionBtn cancelBtn"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : user.approvalStatus === 'approved' ? (
                        <>
                          {user.role !== 'Security Admin' ? (
                            <button
                              onClick={() => handleEditRole(user)}
                              className="actionBtn editBtn"
                            >
                              Edit Role
                            </button>
                          ) : (
                            <span
                              className="actionBtn editBtn disabled"
                              title="Security Admin users cannot have their roles changed"
                              style={{ opacity: 0.5, cursor: 'not-allowed' }}
                            >
                              🔒 Protected
                            </span>
                          )}
                          {user.role !== 'Security Admin' ? (
                            <button
                              onClick={() => deleteUser(user.email, user.role)}
                              className="actionBtn deleteBtn"
                              title="Delete User"
                            >
                              Delete
                            </button>
                          ) : (
                            <span
                              className="actionBtn deleteBtn disabled"
                              title="Security Admin users cannot be deleted"
                              style={{ opacity: 0.5, cursor: 'not-allowed' }}
                            >
                              🔒 Protected
                            </span>
                          )}
                        </>
                      ) : null}

                      {user.approvalStatus === 'rejected' && (
                        <>
                          <button
                            onClick={() => approveUser(user.username)}
                            className="actionBtn approveBtn"
                            title="Approve User"
                          >
                            ✓ Approve
                          </button>
                          {user.role !== 'Security Admin' ? (
                            <button
                              onClick={() => deleteUser(user.email, user.role)}
                              className="actionBtn deleteBtn"
                              title="Delete User"
                            >
                              Delete
                            </button>
                          ) : (
                            <span
                              className="actionBtn deleteBtn disabled"
                              title="Security Admin users cannot be deleted"
                              style={{ opacity: 0.5, cursor: 'not-allowed' }}
                            >
                              🔒 Protected
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{textAlign: 'center'}}>
                  {searchTerm || filterStatus !== 'all' || filterAgency !== 'all' ? 'No users found matching criteria' : 'No users found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      <div className="userStats">
        <p>Total Users: {users.length}</p>
        <p>Filtered Results: {filteredUsers.length}</p>
      </div>

      {/* CSV Import Section - Hidden for now to prevent dirty data */}
      {false && (
        <div className="csvImportSection">
          <div className="sectionHeader">
            <h3>📊 Bulk Import Records</h3>
            <p>Import multiple immigrant records from a CSV file</p>
          </div>

          <div className="csvImportControls">
            <div className="csvFileInput">
              <label htmlFor="csvFile">Select CSV File:</label>
              <input
                type="file"
                id="csvFile"
                accept=".csv"
                onChange={handleCsvFileChange}
                className="modern-input"
                disabled={csvImporting}
              />
              {csvFile && (
                <p className="fileInfo">
                  Selected: {csvFile.name} ({(csvFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>

            <div className="csvActions">
              <button
                onClick={downloadCsvTemplate}
                className="modern-btn template-btn"
                disabled={csvImporting}
              >
                📥 Download Template
              </button>
              <button
                onClick={handleCsvImport}
                className="modern-btn import-btn"
                disabled={!csvFile || csvImporting}
              >
                {csvImporting ? 'Importing...' : '📤 Import CSV'}
              </button>
            </div>
          </div>

          {/* Import Results */}
          {importResults && (
            <div className="importResults">
              <h4>Import Results</h4>
              <div className="resultsSummary">
                <div className="resultStat success">
                  <strong>✅ Successful:</strong> {importResults.successCount} records
                </div>
                <div className="resultStat error">
                  <strong>❌ Failed:</strong> {importResults.errorCount} records
                </div>
                <div className="resultStat total">
                  <strong>📊 Total Processed:</strong> {importResults.totalRows} records
                </div>
              </div>

              {importResults.errors.length > 0 && (
                <div className="errorDetails">
                  <h5>Errors:</h5>
                  <div className="errorList">
                    {importResults.errors.slice(0, 10).map((error, index) => (
                      <div key={index} className="errorItem">
                        <strong>Row {error.row}:</strong> {error.error}
                      </div>
                    ))}
                    {importResults.errors.length > 10 && (
                      <div className="moreErrors">
                        ... and {importResults.errors.length - 10} more errors
                      </div>
                    )}
                  </div>
                </div>
              )}

              {importResults.successfulRecords.length > 0 && (
                <div className="successDetails">
                  <h5>Successfully Imported:</h5>
                  <div className="successList">
                    {importResults.successfulRecords.slice(0, 5).map((record, index) => (
                      <div key={index} className="successItem">
                        Row {record.row}: {record.name} (ID: {record.id})
                    </div>
                    ))}
                    {importResults.successfulRecords.length > 5 && (
                      <div className="moreSuccess">
                        ... and {importResults.successfulRecords.length - 5} more records
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="csvInstructions">
            <h4>CSV Format Instructions:</h4>
            <ul>
              <li>Download the template above to see the required column format</li>
              <li>Required fields: contactDate, ori, agencyName, officerFirstName, officerLastName, badgeOrId, contactCounty, contactStreetAddress, contactCity, contactState, contactZipCode, firstName, lastName, countryOfCitizenship, dateOfBirth, sex</li>
              <li>Date format: YYYY-MM-DD (e.g., 2024-01-15)</li>
              <li>Boolean fields: use "true" or "false"</li>
              <li>Leave optional fields empty if not available</li>
              <li>Maximum file size: 10MB</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
